import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Box,
  styled,
  FormControlLabel,
  Checkbox,
  IconButton,
  CircularProgress,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";

// const server_uri = "http://localhost:5000";
// const server_uri = "https://salonserver-0ojm.onrender.com";
const server_uri = "https://pth-salon-server.fly.dev";

// Styled components
const DashboardContainer = styled(Container)(({ theme }) => ({
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(4),
}));

const LoginContainer = styled(Container)(({ theme }) => ({
  marginTop: theme.spacing(4),
}));

const LoginPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(2),
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

// Styled IconButton for refresh
const RefreshButton = styled(IconButton)(({ theme }) => ({
  marginLeft: theme.spacing(2),
}));

const Dashboard = () => {
  const [pendingAppointments, setPendingAppointments] = useState([]);
  const [checkedInAppointments, setCheckedInAppointments] = useState([]);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState("");
  // State for loading and refreshing

  const [refreshing, setRefreshing] = useState(false);
  const [loading, setLoading] = useState(false); // Loading state

  const navigate = useNavigate();
  const refreshIntervalRef = useRef(null);

  // The useEffect hook itself runs asynchronously in React,
  // meaning the function inside useEffect doesn't block
  // the rendering of the component.
  useEffect(() => {
    if (token) {
      fetchAppointments();
    }
  }, [token]);

  const Location1 = "12990 National Rd.";
  const Location2 = "8515 Refugee Rd.";
  const Location3 = "859 Windmiller Dr.";

  // Add state for storing selected locations
  const [selectedLocations, setSelectedLocations] = useState({
    //Square Brackets for Computed Property Names.
    // Inside an object literal, square brackets allow you to use a variable’s value as a key name.
    [Location1]: true,
    [Location2]: true,
    [Location3]: true,
  });

  // Handle checkbox changes.  This is triggered onChange event
  const handleLocationChange = (event) => {
    const { name, checked } = event.target; // extract name and checked = true/false value form target.
    setSelectedLocations((prev) => ({
      // get prevous locations and destructure array and update the sepcific location.
      ...prev,
      [name]: checked,
    }));
  };

  // Filter appointments based on selected locations
  const filteredPendingAppointments = pendingAppointments.filter(
    (appointment) => selectedLocations[appointment.location] // Filter by location.  appointmet is checked corrosponding to it value in selectedlocation aray.
  );

  const filteredCheckedInAppointments = checkedInAppointments.filter(
    (appointment) => selectedLocations[appointment.location] // Filter by location
  );

  // Fetch appointments from the server
  // Fetch appointments function
  const fetchAppointments = async () => {
    if (!token) return;

    try {
      setRefreshing(true);
      const config = {
        headers: { Authorization: `Bearer ${token}` },
      };

      const [pendingRes, checkedInRes] = await Promise.all([
        axios.get(
          `${server_uri}/api/admin/appointments?status=pending`,
          config
        ),
        axios.get(
          `${server_uri}/api/admin/appointments?status=checked-in`,
          config
        ),
      ]);

      setPendingAppointments(pendingRes.data);
      setCheckedInAppointments(checkedInRes.data);
    } catch (err) {
      console.error("Error fetching appointments:", err);
    } finally {
      setRefreshing(false);
    }
  };

  // Setup interval for auto-refresh and initial fetch
  useEffect(() => {
    if (token) {
      // Initial fetch
      fetchAppointments();

      // Set up interval for auto-refresh every minute (60000 ms)
      refreshIntervalRef.current = setInterval(fetchAppointments, 60000);

      // Cleanup function to clear interval
      return () => {
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current);
        }
      };
    }
  }, [token]);

  // Manual refresh function
  const handleManualRefresh = () => {
    fetchAppointments();
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true); // Start loading spinner
    try {
      const res = await axios.post(`${server_uri}/api/admin/login`, {
        username,
        password,
      });
      setToken(res.data.token);
      setIsAuthenticated(true);
    } catch (err) {
      console.error("Login error:", err);
      alert("Invalid credentials");
    } finally {
      setLoading(false); // Stop loading spinner
    }
  };

  const handleCheckIn = async (id) => {
    try {
      const config = {
        headers: { Authorization: `Bearer ${token}` },
      };

      await axios.put(
        `${server_uri}/api/admin/appointments/${id}`,
        { status: "checked-in" },
        config
      );
      fetchAppointments();
    } catch (err) {
      console.error("Check-in error:", err);
    }
  };

  const [editingAppointment, setEditingAppointment] = useState(null);

  const handleEdit = (appointmentId) => {
    const appointmentToEdit = pendingAppointments.find(
      (appt) => appt._id === appointmentId
    );
    if (appointmentToEdit) {
      // This line uses the spread operator (...) to create a shallow copy of the
      // appointmentToEdit object and sets it as the new state for editingAppointment.
      setEditingAppointment({ ...appointmentToEdit }); // make a copy to edit
      console.log(`Appointment to edit ${appointmentToEdit}`);
    }
  };

  const handleSaveEdit = async (id) => {
    console.log("Saving updated appointment:", editingAppointment, " ID: ", id);
    try {
      const config = {
        headers: { Authorization: `Bearer ${token}` },
      };

      const response = await axios.put(
        `${server_uri}/api/admin/appointments/${id}`,
        {
          username: editingAppointment.username,
          email: editingAppointment.email,
          contactNumber: editingAppointment.contactNumber,
        },
        config
      );
      console.log("Save response:", response.data, id);

      setEditingAppointment(null); // Exit edit mode
      fetchAppointments(); // Refresh the appointment list
    } catch (err) {
      console.error("Edit save error:", err);
    }
  };

  const handleDelete = async (id) => {
    try {
      const config = {
        headers: { Authorization: `Bearer ${token}` },
      };

      await axios.delete(`${server_uri}/api/admin/appointments/${id}`, config);
      fetchAppointments(); // Refresh the list
    } catch (err) {
      console.error("Delete error:", err);
    }
  };

  if (!isAuthenticated) {
    return (
      <LoginContainer maxWidth="sm">
        <LoginPaper elevation={3}>
          <Typography variant="h4" gutterBottom>
            Admin Login
          </Typography>
          <form onSubmit={handleLogin}>
            <TextField
              fullWidth
              label="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              margin="normal"
              required
              variant="outlined"
            />
            <TextField
              fullWidth
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
              variant="outlined"
            />
            <Box mt={2}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                disabled={loading} // Disable button while loading
              >
                {loading ? (
                  <CircularProgress size={24} color="secondary" /> // Show spinner while loading
                ) : (
                  "Login"
                )}
              </Button>
            </Box>
          </form>
        </LoginPaper>
      </LoginContainer>
    );
  }

  return (
    <DashboardContainer maxWidth="lg">
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="h4" gutterBottom>
          Appointment Dashboard
        </Typography>
        <Box display="flex" alignItems="center">
          {refreshing && <CircularProgress size={20} sx={{ mr: 1 }} />}
          <RefreshButton
            onClick={handleManualRefresh}
            color="primary"
            title="Refresh appointments"
          >
            <RefreshIcon />
          </RefreshButton>
        </Box>
      </Box>

      {/* Location Filters (Checkboxes) */}
      <Box mb={3}>
        <FormControlLabel
          control={
            <Checkbox
              checked={selectedLocations[Location1]}
              onChange={handleLocationChange}
              name={Location1}
            />
          }
          label={Location1}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={selectedLocations[Location2]}
              onChange={handleLocationChange}
              name={Location2}
            />
          }
          label={Location2}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={selectedLocations[Location3]}
              onChange={handleLocationChange}
              name={Location3}
            />
          }
          label={Location3}
        />
      </Box>

      {/* Pending Appointments Table */}
      <SectionTitle variant="h5">Pending Appointments</SectionTitle>
      <StyledTableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>

              {/* <TableCell>Email</TableCell>
              <TableCell>Contact</TableCell> */}
              <TableCell>Location</TableCell>
              <TableCell>Services</TableCell>
              <TableCell>Created Date</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredPendingAppointments.map((appointment) => (
              <TableRow key={appointment._id} hover>
                <TableCell>
                  {editingAppointment?._id === appointment._id ? (
                    <TextField
                      value={editingAppointment?.username || ""}
                      onChange={(e) =>
                        setEditingAppointment((prev) => ({
                          ...prev,
                          username: e.target.value,
                        }))
                      }
                      size="small"
                    />
                  ) : (
                    appointment.username
                  )}
                </TableCell>

                {/* <TableCell>
                  {editingAppointment?._id === appointment._id ? (
                    <TextField
                      value={editingAppointment?.email || ""}
                      onChange={(e) =>
                        setEditingAppointment((prev) => ({
                          ...prev,
                          email: e.target.value,
                        }))
                      }
                      size="small"
                    />
                  ) : (
                    appointment.email
                  )}
                </TableCell> */}

                {/* <TableCell>
                  {editingAppointment?._id === appointment._id ? (
                    <TextField
                      value={editingAppointment?.contactNumber || ""}
                      onChange={(e) =>
                        setEditingAppointment((prev) => ({
                          ...prev,
                          contactNumber: e.target.value,
                        }))
                      }
                      size="small"
                    />
                  ) : (
                    appointment.contactNumber
                  )}
                </TableCell> */}

                <TableCell>{appointment.location}</TableCell>
                <TableCell>{appointment.services.join(", ")}</TableCell>
                <TableCell>
                  {appointment.createdAt
                    ? new Date(appointment.createdAt).toLocaleDateString(
                        "en-US",
                        {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        }
                      )
                    : "N/A"}
                </TableCell>
                <TableCell>
                  <Box display="flex" gap={1}>
                    {editingAppointment?._id === appointment._id ? (
                      <Button
                        variant="contained"
                        color="success"
                        size="small"
                        onClick={() => handleSaveEdit(appointment._id)}
                      >
                        Save
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        color="secondary"
                        size="small"
                        onClick={() => handleEdit(appointment._id)}
                      >
                        Edit
                      </Button>
                    )}
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => handleCheckIn(appointment._id)}
                      size="small"
                    >
                      Check-In
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => handleDelete(appointment._id)}
                      size="small"
                    >
                      Delete
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </StyledTableContainer>

      <SectionTitle variant="h5">Checked-In Appointments</SectionTitle>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Services</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredCheckedInAppointments.map((appointment) => (
              <TableRow key={appointment._id} hover>
                <TableCell>{appointment.username}</TableCell>
                <TableCell>{appointment.email}</TableCell>
                <TableCell>{appointment.contactNumber}</TableCell>
                <TableCell>{appointment.location}</TableCell>
                <TableCell>{appointment.services.join(", ")}</TableCell>
                <TableCell>
                  <Box display="flex" gap={1}>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => handleDelete(appointment._id)}
                      size="small"
                    >
                      Delete
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </DashboardContainer>
  );
};

export default Dashboard;
